import React from 'react';
import { cn } from '@shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { TabsBadge } from '@shared/ui';

const sidebarMenuButtonVariants = cva(
  'flex h-12 w-full max-w-[var(--layout-sidebar-menu-width,234px)] shrink-0 cursor-pointer items-center gap-3 rounded-2xl px-4 py-3 font-sans text-sm font-medium leading-none transition-[background-color,color,padding,width] duration-normal ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
  {
    variants: {
      variant: {
        default: 'text-sidebar-foreground',
        active:
          'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent',
        collapse:
          'text-sidebar-foreground hover:bg-sidebar-accent/50',
      },
      collapsed: {
        true: 'w-full justify-center px-3',
        false: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      collapsed: false,
    },
  }
);

interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sidebarMenuButtonVariants> {
  icon?: React.ReactNode;
  label?: string;
  isActive?: boolean;
  collapsed?: boolean;
  badge?: number;
}

export const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonProps
>(
  (
    {
      className,
      variant,
      icon,
      label,
      isActive,
      collapsed,
      badge,
      children,
      ...props
    },
    ref
  ) => {
    const actualVariant = isActive ? 'active' : variant;
    const isSidebarCollapsed = Boolean(collapsed);
    const labelContent = label || children;

    return (
      <button
        ref={ref}
        className={cn(
          sidebarMenuButtonVariants({
            variant: actualVariant,
            collapsed: isSidebarCollapsed,
          }),
          className
        )}
        {...props}
      >
        {icon && (
          <div
            className={cn(
              'flex size-6 shrink-0 items-center justify-center transition-[color,transform] duration-normal ease-standard [&_svg]:size-5',
              actualVariant === 'active'
                ? 'text-sidebar-icon-active [&_svg]:size-6'
                : 'text-sidebar-foreground'
            )}
          >
            {icon}
          </div>
        )}
        {labelContent != null && labelContent !== false && (
          <span
            className={cn(
              'min-w-0 overflow-hidden whitespace-nowrap text-left text-sm font-[inherit] leading-none',
              'transition-[max-width,opacity,margin] duration-normal ease-standard',
              isSidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-full flex-1 opacity-100'
            )}
          >
            {labelContent}
          </span>
        )}
        {typeof badge === 'number' && badge > 0 && !isSidebarCollapsed ? <TabsBadge>{badge > 99 ? '99+' : badge}</TabsBadge> : null}
      </button>
    );
  }
);

SidebarMenuButton.displayName = 'SidebarMenuButton';
